const hasValue = (value) => value !== null && value !== undefined && String(value).trim() !== '';

export const resolveLinkedTradeId = (position) => {
  if (!position || typeof position !== 'object') return null;
  if (hasValue(position.trade_id)) return position.trade_id;
  if (position.trade_status && hasValue(position.id)) return position.id;
  return null;
};

const getTicket = (position) => (
  position?.ticket
  ?? position?.position_id
  ?? position?.platform_position_id
  ?? position?.id
);

const getSymbol = (position) => position?.symbol ?? position?.instrument;

const getVolume = (position) => (
  position?.volume
  ?? position?.lot_size
  ?? position?.remaining_size
);

const getDirection = (position) => {
  const direction = position?.direction ?? position?.side;
  return typeof direction === 'string' ? direction.toUpperCase() : direction;
};

export const buildOrphanClosePayload = (position, fallbackAccountId = null) => {
  const payload = {
    account_id: position?.accountId ?? position?.account_id ?? position?.account ?? fallbackAccountId,
    ticket: getTicket(position),
    symbol: getSymbol(position),
    volume: getVolume(position),
    direction: getDirection(position),
  };

  const missing = Object.entries(payload)
    .filter(([, value]) => !hasValue(value))
    .map(([key]) => key);

  if (missing.length > 0) {
    throw new Error(`Missing close details: ${missing.join(', ')}`);
  }

  return payload;
};

export const canCloseOpenPosition = (position, fallbackAccountId = null) => {
  if (resolveLinkedTradeId(position)) return true;
  try {
    buildOrphanClosePayload(position, fallbackAccountId);
    return true;
  } catch {
    return false;
  }
};

export const closeOpenPosition = async (
  position,
  {
    closeTradeApi,
    closeOrphanPositionApi,
    fallbackAccountId = null,
    tradeId = null,
  } = {},
) => {
  const linkedTradeId = tradeId || resolveLinkedTradeId(position);
  if (linkedTradeId) {
    if (typeof closeTradeApi !== 'function') {
      throw new Error('Close trade API is not available.');
    }
    return closeTradeApi(linkedTradeId, { close_reason: 'Manual' });
  }

  if (typeof closeOrphanPositionApi !== 'function') {
    throw new Error('Close broker position API is not available.');
  }
  return closeOrphanPositionApi(buildOrphanClosePayload(position, fallbackAccountId));
};

export const closeOpenPositionsSequentially = async (positions, options) => {
  const list = Array.isArray(positions) ? positions : [];
  const successes = [];
  const failures = [];

  for (const position of list) {
    try {
      const response = await closeOpenPosition(position, options);
      successes.push({ position, response });
    } catch (error) {
      failures.push({ position, error });
    }
  }

  return {
    total: list.length,
    successes,
    failures,
  };
};

export const getPositionCloseErrorMessage = (error) => (
  error?.data?.detail
  || error?.response?.data?.detail
  || error?.message
  || 'Please try again.'
);
